<template>
    <ClientOnly>
        <VFooter class="pa-0 footer-root" :order="1" flat absolute app :style="backgroundStyle">
            <div class="footer-inner w-100">
                <!-- Top separator -->
                <div class="footer-top-line" />

                <VContainer>
                    <!-- CTA -->
                    <div class="footer-cta">
                        <div class="footer-cta-copy">
                            <div class="footer-cta-kicker">
                                Ready when you are
                            </div>

                            <div class="footer-cta-title">
                                Start building your transformation.
                            </div>

                            <div class="footer-cta-description">
                                Tell us what you want to achieve and we'll help you work out the next step.
                            </div>
                        </div>

                        <VBtn color="primary" rounded="pill" size="large"
                            :variant="theme.global.name.value === 'dark' ? 'tonal' : 'flat'" class="footer-cta-button"
                            @click="scrollToContact">
                            <template #prepend>
                                <VIcon icon="$mdiLightningBolt" size="19" />
                            </template>

                            Enquire about coaching
                        </VBtn>
                    </div>

                    <!-- Main footer -->
                    <div class="footer-main">
                        <!-- Brand -->
                        <div class="footer-brand">
                            <NuxtLink to="/" class="footer-logo text-decoration-none">
                                <AppLogo />
                            </NuxtLink>

                            <p class="footer-description">
                                {{ config.public.appFooterDescription }}
                            </p>

                            <div class="footer-contact">
                                <a :href="`tel:${config.public.appContactNumber44}`" class="footer-contact-item">
                                    <div class="footer-contact-icon">
                                        <VIcon icon="$mdiPhoneOutline" size="18" />
                                    </div>

                                    <div>
                                        <div class="footer-contact-label">
                                            Call us
                                        </div>

                                        <div class="footer-contact-value">
                                            {{ config.public.appContactNumberFormatted }}
                                        </div>
                                    </div>
                                </a>

                                <a :href="`mailto:${config.public.appContactEmail}`" class="footer-contact-item">
                                    <div class="footer-contact-icon">
                                        <VIcon icon="$mdiEmailOutline" size="18" />
                                    </div>

                                    <div>
                                        <div class="footer-contact-label">
                                            Email us
                                        </div>

                                        <div class="footer-contact-value">
                                            {{ config.public.appContactEmail }}
                                        </div>
                                    </div>
                                </a>
                            </div>

                            <!-- Social buttons -->
                            <div v-if="socials.length" class="footer-socials">
                                <a v-for="social in socials" :key="social.label" :href="social.to" target="_blank"
                                    rel="noopener noreferrer" class="footer-social" :aria-label="social.label"
                                    :title="social.label">
                                    <VIcon :icon="social.icon" size="21" />
                                </a>
                            </div>
                        </div>

                        <!-- Desktop navigation -->
                        <div v-if="mdAndUp" class="footer-navigation">
                            <div class="footer-column">
                                <div class="footer-column-title">
                                    Explore
                                </div>

                                <NuxtLink v-for="link in footerLinks1" :key="link.to" :to="link.to"
                                    class="footer-nav-link">
                                    {{ link.text }}

                                    <VIcon icon="$mdiArrowTopRight" size="13" class="footer-link-arrow" />
                                </NuxtLink>
                            </div>

                            <div class="footer-column">
                                <div class="footer-column-title">
                                    Members
                                </div>

                                <NuxtLink v-for="link in footerLinks2" :key="link.to" :to="link.to"
                                    class="footer-nav-link">
                                    {{ link.text }}

                                    <VIcon icon="$mdiArrowTopRight" size="13" class="footer-link-arrow" />
                                </NuxtLink>
                            </div>

                            <div class="footer-column">
                                <div class="footer-column-title">
                                    Socials
                                </div>

                                <a v-for="link in footerLinks3" :key="link.to" :href="link.to" target="_blank"
                                    rel="noopener noreferrer" class="footer-nav-link">
                                    {{ link.text }}

                                    <VIcon icon="$mdiArrowTopRight" size="13" class="footer-link-arrow" />
                                </a>
                            </div>
                        </div>
                    </div>

                    <!-- Mobile navigation -->
                    <div v-if="!mdAndUp" class="footer-mobile-nav">
                        <VExpansionPanels variant="accordion" class="footer-panels">
                            <VExpansionPanel v-for="group in mobileGroups" :key="group.title" class="footer-panel"
                                elevation="0">
                                <template #title>
                                    <span class="footer-panel-title">
                                        {{ group.title }}
                                    </span>
                                </template>

                                <template #text>
                                    <div class="footer-panel-links">
                                        <template v-for="link in group.links" :key="link.to">
                                            <a v-if="group.external" :href="link.to" target="_blank"
                                                rel="noopener noreferrer" class="footer-nav-link">
                                                {{ link.text }}
                                            </a>

                                            <NuxtLink v-else :to="link.to" class="footer-nav-link">
                                                {{ link.text }}
                                            </NuxtLink>
                                        </template>
                                    </div>
                                </template>
                            </VExpansionPanel>
                        </VExpansionPanels>
                    </div>

                    <VDivider />

                    <!-- Bottom -->
                    <div class="footer-copyright footer-bottom">
                        <div>
                            © {{ year }}
                            {{ config.public.appCompanyName || config.public.appName }}.
                            All rights reserved.
                        </div>

                        <div>
                            <VIcon icon="$mdiMapMarkerOutline" size="16" />

                            <span>
                                Private personal training and coaching in St Helens, serving Liverpool, Rainhill and
                                surrounding areas.
                            </span>
                        </div>
                    </div>
                </VContainer>
            </div>
        </VFooter>
    </ClientOnly>
</template>

<script setup lang="ts">
import { useDisplay, useTheme } from 'vuetify'
import { useBackgroundStyle } from '@R/constants/componentStyle'

const config = useRuntimeConfig()

const { mdAndUp } = useDisplay()

const backgroundStyle = useBackgroundStyle()

const theme = useTheme()

const year = new Date().getFullYear()

const socials = computed(() => {
    const fb =
        String(config.public.appSocialsFacebook || '').trim()

    const ig =
        String(config.public.appSocialsInstagram || '').trim()

    return [
        ...(fb
            ? [
                {
                    label: 'Facebook',
                    icon: '$mdiFacebook',
                    to: fb,
                },
            ]
            : []),

        ...(ig
            ? [
                {
                    label: 'Instagram',
                    icon: '$mdiInstagram',
                    to: ig,
                },
            ]
            : []),
    ]
})

const footerLinks1 = [
    {
        text: 'Home',
        to: '/',
    },
    {
        text: 'Transformations',
        to: '/results/transformations',
    },
    {
        text: 'Testimonials',
        to: '/results/testimonials',
    },
    {
        text: 'Contact',
        to: '/contact',
    },
]

const footerLinks2 = [
    {
        text: 'Login',
        to: '/auth/login',
    },
]

const footerLinks3 = computed(() => [
    ...(config.public.appSocialsFacebook
        ? [
            {
                text: 'Facebook',
                to: config.public.appSocialsFacebook,
            },
        ]
        : []),

    ...(config.public.appSocialsInstagram
        ? [
            {
                text: 'Instagram',
                to: config.public.appSocialsInstagram,
            },
        ]
        : []),
])

const mobileGroups = computed(() => [
    {
        title: 'Explore',
        links: footerLinks1,
        external: false,
    },
    {
        title: 'Members',
        links: footerLinks2,
        external: false,
    },
    {
        title: 'Socials',
        links: footerLinks3.value,
        external: true,
    },

])

const keywords = `
the bunker fitness liverpool,
the bunker fitness st helens,
the bunker gym liverpool,
the bunker gym st helens,
personal trainer liverpool,
personal trainer st helens,
personal trainer rainhill,
private gym liverpool,
private gym st helens,
private gym rainhill,
personal training liverpool,
personal training st helens,
personal training rainhill,
1 to 1 personal training liverpool,
1 to 1 personal training st helens,
small group personal training liverpool,
small group personal training st helens,
small group personal training rainhill,
strength training liverpool,
strength training st helens,
strength coach liverpool,
strength coach st helens,
strength coach rainhill,
strength and conditioning liverpool,
strength and conditioning st helens,
fat loss coach liverpool,
fat loss coach st helens,
fat loss coach rainhill,
weight loss personal trainer liverpool,
weight loss personal trainer st helens,
body transformation liverpool,
body transformation st helens,
body transformation rainhill,
nutrition coach liverpool,
nutrition coach st helens,
nutrition coach rainhill,
muscle building coach liverpool,
muscle building coach st helens,
bodybuilding coach liverpool,
bodybuilding coach st helens
`
    .split(',')
    .map(keyword => keyword.trim())
    .filter(Boolean)

const scrollToContact = async () => {
    if (import.meta.client) {
        const contact =
            document.getElementById('contact')

        if (contact) {
            contact.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })

            return
        }
    }

    await navigateTo('/#contact')
}
</script>

<style scoped>
.footer-root,
.footer-inner {
    width: 100%;
    max-width: 100%;

    overflow-x: clip;
}

.footer-root {
    position: relative;
}

.footer-inner {
    position: relative;

    padding-bottom: 14px;
}

/* --------------------------------
   Top edge
-------------------------------- */

.footer-top-line {
    width: 100%;
    height: 1px;

    background:
        linear-gradient(to right,
            transparent,
            rgba(var(--v-theme-primary), 0.28),
            rgba(var(--v-theme-on-surface), 0.08),
            transparent);
}

/* --------------------------------
   CTA
-------------------------------- */

.footer-cta {
    position: relative;

    margin-top: 34px;
    margin-bottom: 42px;

    padding: 24px 28px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 28px;

    overflow: hidden;

    border-radius: 22px;

    background:
        linear-gradient(135deg,
            rgba(var(--v-theme-primary), 0.10),
            rgba(var(--v-theme-surface), 0.52));

    border:
        1px solid rgba(var(--v-theme-primary), 0.13);

    box-shadow:
        0 14px 42px rgba(0, 0, 0, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.04);

    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
}

.footer-cta::after {
    content: '';

    position: absolute;

    width: 240px;
    height: 240px;

    right: -130px;
    top: -150px;

    border-radius: 50%;

    background:
        rgb(var(--v-theme-primary));

    filter: blur(100px);

    opacity: 0.08;

    pointer-events: none;
}

.footer-cta-copy {
    position: relative;
    z-index: 1;

    max-width: 650px;
}

.footer-cta-kicker {
    margin-bottom: 5px;

    color:
        rgb(var(--v-theme-primary));

    font-size: 0.68rem;
    font-weight: 800;

    letter-spacing: 0.08em;

    text-transform: uppercase;
}

.footer-cta-title {
    font-size: 1.25rem;
    font-weight: 800;

    letter-spacing: -0.015em;
}

.footer-cta-description {
    margin-top: 5px;

    color:
        rgba(var(--v-theme-on-surface), 0.58);

    font-size: 0.82rem;
}

.footer-cta-button {
    position: relative;
    z-index: 1;

    flex-shrink: 0;

    font-weight: 700;
}

/* --------------------------------
   Main
-------------------------------- */

.footer-main {
    display: grid;

    grid-template-columns:
        minmax(260px, 1.15fr) minmax(0, 2fr);

    gap: 60px;

    padding-bottom: 40px;
}

/* --------------------------------
   Brand
-------------------------------- */

.footer-brand {
    max-width: 350px;
}

.footer-logo {
    display: inline-flex;

    color: inherit;
}

.footer-description {
    max-width: 330px;

    margin-top: 18px;
    margin-bottom: 22px;

    color:
        rgba(var(--v-theme-on-surface), 0.60);

    font-size: 0.84rem;
    line-height: 1.7;
}

/* --------------------------------
   Contact
-------------------------------- */

.footer-contact {
    display: flex;
    flex-direction: column;

    gap: 10px;
}

.footer-contact-item {
    display: flex;
    align-items: center;

    gap: 10px;

    width: fit-content;

    color: inherit;
    text-decoration: none;

    transition:
        transform 0.18s ease,
        color 0.18s ease;
}

.footer-contact-item:hover {
    transform: translateX(3px);

    color:
        rgb(var(--v-theme-primary));
}

.footer-contact-icon {
    width: 34px;
    min-width: 34px;
    height: 34px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 10px;

    color:
        rgb(var(--v-theme-primary));

    background:
        rgba(var(--v-theme-primary), 0.07);

    border:
        1px solid rgba(var(--v-theme-primary), 0.10);
}

.footer-contact-label {
    color:
        rgba(var(--v-theme-on-surface), 0.46);

    font-size: 0.65rem;
}

.footer-contact-value {
    margin-top: 1px;

    font-size: 0.78rem;
    font-weight: 650;
}

/* --------------------------------
   Social buttons
-------------------------------- */

.footer-socials {
    display: flex;

    gap: 9px;

    margin-top: 20px;
}

.footer-social {
    width: 40px;
    height: 40px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 12px;

    color:
        rgba(var(--v-theme-on-surface), 0.62);

    background:
        rgba(var(--v-theme-surface), 0.42);

    border:
        1px solid rgba(var(--v-theme-on-surface), 0.08);

    transition:
        transform 0.2s ease,
        color 0.2s ease,
        border-color 0.2s ease,
        background 0.2s ease;
}

.footer-social:hover {
    transform: translateY(-3px);

    color:
        rgb(var(--v-theme-primary));

    border-color:
        rgba(var(--v-theme-primary), 0.25);

    background:
        rgba(var(--v-theme-primary), 0.07);
}

/* --------------------------------
   Navigation
-------------------------------- */

.footer-navigation {
    display: grid;

    grid-template-columns:
        1.2fr 1fr 1fr 1fr;

    gap: 26px;
}

.footer-column {
    display: flex;
    flex-direction: column;

    align-items: flex-start;
}

.footer-column-title {
    margin-bottom: 14px;

    color:
        rgba(var(--v-theme-on-surface), 0.42);

    font-size: 0.68rem;
    font-weight: 800;

    letter-spacing: 0.08em;

    text-transform: uppercase;
}

.footer-nav-link {
    position: relative;

    display: inline-flex;
    align-items: center;

    gap: 4px;

    margin: 5px 0;

    color:
        rgba(var(--v-theme-on-surface), 0.70);

    font-size: 0.8rem;
    font-weight: 550;

    text-decoration: none;

    transition:
        color 0.18s ease,
        transform 0.18s ease;
}

.footer-nav-link:hover {
    color:
        rgb(var(--v-theme-primary));

    transform: translateX(3px);
}

.footer-link-arrow {
    opacity: 0;

    transform:
        translate(-3px, 3px);

    transition:
        opacity 0.18s ease,
        transform 0.18s ease;
}

.footer-nav-link:hover .footer-link-arrow {
    opacity: 1;

    transform: translate(0);
}

/* --------------------------------
   Mobile navigation
-------------------------------- */

.footer-mobile-nav {
    margin-top: 28px;
    margin-bottom: 32px;
}

.footer-panels {
    display: flex;
    flex-direction: column;

    gap: 7px;
}

.footer-panel {
    overflow: hidden;

    border-radius: 13px !important;

    background:
        rgba(var(--v-theme-surface), 0.32) !important;

    border:
        1px solid rgba(var(--v-theme-on-surface), 0.07);
}

.footer-panel :deep(.v-expansion-panel__shadow) {
    display: none;
}

.footer-panel :deep(.v-expansion-panel-title) {
    min-height: 53px;

    padding: 12px 15px;
}

.footer-panel :deep(.v-expansion-panel-text__wrapper) {
    padding:
        5px 15px 14px;
}

.footer-panel-title {
    font-size: 0.84rem;
    font-weight: 700;
}

.footer-panel-links {
    display: grid;

    grid-template-columns:
        repeat(2, minmax(0, 1fr));

    gap: 2px 12px;
}

/* --------------------------------
   Bottom
-------------------------------- */

.footer-bottom {
    min-height: 70px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 16px;
}

.footer-copyright {
    color:
        rgba(var(--v-theme-on-surface), 0.42);

    font-size: 0.68rem;
}

.footer-bottom-links {
    display: flex;
    align-items: center;

    gap: 10px;
}

.footer-bottom-link {
    color:
        rgba(var(--v-theme-on-surface), 0.45);

    font-size: 0.68rem;

    text-decoration: none;

    transition:
        color 0.18s ease;
}

.footer-bottom-link:hover {
    color:
        rgb(var(--v-theme-primary));
}

.footer-bottom-dot {
    width: 3px;
    height: 3px;

    border-radius: 50%;

    background:
        rgba(var(--v-theme-on-surface), 0.25);
}

/* --------------------------------
   Tablet
-------------------------------- */

@media (max-width: 959px) {
    .footer-main {
        display: block;

        padding-bottom: 0;
    }

    .footer-brand {
        max-width: 500px;
    }
}

/* --------------------------------
   Mobile
-------------------------------- */

@media (max-width: 599px) {
    .footer-cta {
        margin-top: 24px;
        margin-bottom: 32px;

        padding: 20px;

        flex-direction: column;
        align-items: stretch;

        gap: 18px;

        border-radius: 18px;
    }

    .footer-cta-title {
        font-size: 1.1rem;
    }

    .footer-cta-description {
        font-size: 0.78rem;
        line-height: 1.55;
    }

    .footer-cta-button {
        width: 100%;
    }

    .footer-description {
        margin-top: 15px;
        margin-bottom: 18px;

        font-size: 0.8rem;
    }

    .footer-contact-value {
        font-size: 0.74rem;
    }

    .footer-socials {
        margin-top: 18px;
    }

    .footer-keyword-section {
        padding: 20px 0;
    }

    .footer-keywords {
        gap: 5px;
    }

    .footer-keyword {
        padding: 3px 6px;

        font-size: 0.56rem;
    }

    .footer-bottom {
        min-height: 78px;

        padding: 17px 0;

        flex-direction: column;
        justify-content: center;

        gap: 8px;

        text-align: center;
    }
}
</style>